/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IAttributeSwatch,
    IAttributeValue,
    IProductSearchResult
} from '@msdyn365-commerce/commerce-entities/src/custom-entities/product-list';
import { ITelemetry } from '@msdyn365-commerce/core';
import {
    AttributeDataType,
    AttributeSwatch,
    AttributeValue,
    CommerceProperty,
    ProductRefinerValue,
    ProductSearchResult,
    RefinerType,
    SortColumn
} from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';

import { IExtensionProperty, IRefinerHierarchy, IRefinerValue } from '../models/product-refiners';
import { IRefineItemData, IRefineItemToggleNotification, IRefineSubMenuData } from './common';
import { ISliderThumbProps, ISliderLabel } from '@msdyn365-commerce-modules/utilities';

export const getSliderThumbs = (
    sliderClassName: string,
    selectedMin: string,
    selectedMinPrice: string,
    selectedMax: string | undefined,
    selectedMaxPrice: string,
    maxPrice: string,
    minValueSliderThumbAriaLabel?: string,
    maxValueSliderThumbAriaLabel?: string
): ISliderThumbProps[] => {
    return [
        {
            id: `${sliderClassName} slider_thumb_start`,
            value: (selectedMin && Number(selectedMin)) || 0,
            ariaLabel: `${minValueSliderThumbAriaLabel!}`,
            ariaValueText: `${selectedMinPrice}`
        },
        {
            id: `${sliderClassName} slider_thumb_end`,
            value: Number(selectedMax) || Number(maxPrice),
            ariaLabel: `${maxValueSliderThumbAriaLabel!}`,
            ariaValueText: `${selectedMaxPrice}`
        }
    ];
};

export const getSliderLabels = (sliderClassname: string, minPrice: string, maxPrice: string): ISliderLabel[] => {
    return [
        {
            labelId: `${sliderClassname} slider_label_start`,
            labelString: `${minPrice}`,
            labelPositioning: 'start'
        },
        {
            labelId: `${sliderClassname} slider_label_end`,
            labelString: `${maxPrice}`,
            labelPositioning: 'end'
        }
    ];
};

export function getRangeType(subMenuData: IRefineSubMenuData): 'input' | 'slider' {
    if (subMenuData.displayTemplateValue === 3 || subMenuData.displayTemplateValue === 1 || subMenuData.displayTemplateValue === 2) {
        return 'slider';
    }
    return 'input';
}

/**
 * Checks if the data type value corresponds to a slider.
 * @param dataTypeValue - DataTypeValue.
 * @returns - Whether the dataTypeValue is range type or not.
 */
export function isRangeType(dataTypeValue: number | undefined): boolean {
    return (
        dataTypeValue === AttributeDataType.Currency ||
        dataTypeValue === AttributeDataType.Decimal ||
        dataTypeValue === AttributeDataType.Integer
    );
}

/**
 * Find the refinement criterion associated with this product refiner value.
 * @param productRefinerValue - Product refiner value to match.
 * @param refinementCriterion - Selected refinement criteria.
 * @returns - Boolean.
 */
export function isMatchingRefinementCriterion(productRefinerValue: IRefineItemData, refinementCriterion: IRefineItemData): boolean {
    // If the value is a range, then match only on data type value; otherwise match on item string
    return (
        refinementCriterion.refinerItemId === productRefinerValue.refinerItemId &&
        refinementCriterion.sourceValue === productRefinerValue.sourceValue &&
        refinementCriterion.dataTypeValue === productRefinerValue.dataTypeValue &&
        (isRangeType(refinementCriterion.dataTypeValue) || refinementCriterion.name === productRefinerValue.name)
    );
}

/**
 * Find the refinement criterion associated with this product refiner value.
 * @param productRefinerValue - Product refiner value to match.
 * @param refinementCriteria - Selected refinement criteria.
 * @returns - Refine Item data.
 */
export function findMatchingRefinementCriterion(
    productRefinerValue: IRefineItemData,
    refinementCriteria: IRefineItemData[]
): IRefineItemData | undefined {
    // If the value is a range, then match only on data type value; otherwise match on item string
    return refinementCriteria.find(refinementCriterion => isMatchingRefinementCriterion(productRefinerValue, refinementCriterion));
}

/**
 * Get updated refinement criteria.
 * @param itemToggleNotification - RefineItemToggleNotification object.
 * @param currentRefinementCriteria - Array of IRefineItemData.
 * @returns - Array of IRefineItemData.
 */
export function getUpdatedRefinementCriteria(
    itemToggleNotification: IRefineItemToggleNotification,
    currentRefinementCriteria: IRefineItemData[]
): IRefineItemData[] {
    const updatedRefinementCriteria: IRefineItemData[] = [];
    let isToggledItemFound = false;
    for (const selectedCriterion of currentRefinementCriteria) {
        if (isMatchingRefinementCriterion(itemToggleNotification.refinerItemData, selectedCriterion)) {
            isToggledItemFound = true;
            if (itemToggleNotification.isSelecting) {
                const next = {
                    ...selectedCriterion,
                    minValue:
                        itemToggleNotification.rangeStart !== undefined
                            ? `${itemToggleNotification.rangeStart}`
                            : selectedCriterion.minValue,
                    maxValue:
                        itemToggleNotification.rangeEnd !== undefined ? `${itemToggleNotification.rangeEnd}` : selectedCriterion.maxValue
                };
                updatedRefinementCriteria.push(next);
            } // Else the item is being de-selected, so omit it from the refinement criteria
        } else {
            // Keep existing criterion because it is not in the item toggle notification
            updatedRefinementCriteria.push(selectedCriterion);
        }
    }

    if (!isToggledItemFound) {
        const next = {
            ...itemToggleNotification.refinerItemData,
            minValue:
                itemToggleNotification.rangeStart !== undefined
                    ? `${itemToggleNotification.rangeStart}`
                    : itemToggleNotification.refinerItemData.name,
            maxValue:
                itemToggleNotification.rangeEnd !== undefined
                    ? `${itemToggleNotification.rangeEnd}`
                    : itemToggleNotification.refinerItemData.name
        };
        updatedRefinementCriteria.push(next);

        // If single select, then deselect any others in the parent refiner group
        if (
            (itemToggleNotification.refinerItemData.dataTypeValue === AttributeDataType.Text ||
                itemToggleNotification.refinerItemData.dataTypeValue === AttributeDataType.TrueFalse) &&
            itemToggleNotification.refinerSubMenuData.refinerTypeValue === RefinerType.SingleSelect
        ) {
            for (const child of itemToggleNotification.refinerSubMenuData.values) {
                if (!(child.refinerItemId === next.refinerItemId && child.name === next.name)) {
                    const matchingIndex = updatedRefinementCriteria.findIndex(criterion => isMatchingRefinementCriterion(child, criterion));
                    // eslint-disable-next-line max-depth -- Condition required.
                    if (matchingIndex > -1) {
                        updatedRefinementCriteria.splice(matchingIndex, 1);
                    }
                }
            }
        }
    }

    return updatedRefinementCriteria;
}

/**
 * Format Price.
 * @param amount - Amount.
 * @param currency - Currency.
 * @param locale - Locale.
 * @param telemetry - Telemetry.
 * @returns - Formatted Price.
 */
export function formatPrice(
    amount: string | undefined,
    currency: string | undefined,
    locale: string | undefined,
    telemetry: ITelemetry
): string {
    if (!amount || !currency) {
        telemetry.trace(`[refine-menu.utilities.formatPrice] could not format price for ${amount!} ${currency!}`);
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- For string it is required.
        return amount || '';
    }
    const priceAmount = (amount && Number(amount)) || 0;
    let result: string;

    try {
        result = new Intl.NumberFormat(locale, {
            style: 'currency',
            currencyDisplay: 'symbol',
            currency,
            minimumFractionDigits: 0
        }).format(priceAmount);
    } catch (error) {
        result = `${priceAmount} ${currency}`;
        telemetry.warning(`[refine-menu.utilities.formatPrice] Failed to format price for ${result}: ${error}`);
    }

    return result;
}

/**
 * Convert an array of RefineItemData to ProductRefinerValue.
 * @param productRefinerValue - RefineItemData.
 * @returns - ProductRefinerValue[].
 */
export const convertProductRefinerValueToRefinerItemData = (productRefinerValue: ProductRefinerValue[]): IRefineItemData[] => {
    const refinerItems: IRefineItemData[] = [];
    for (const productRefiner of productRefinerValue) {
        refinerItems.push({
            count: productRefiner.Count,
            dataTypeValue: productRefiner.DataTypeValue,
            refinerItemId: productRefiner.RefinerRecordId ?? 0,
            unitText: productRefiner.UnitText,
            rowNumber: productRefiner.RowNumber,
            sourceValue: productRefiner.RefinerSourceValue,
            swatchColorHexCode: productRefiner.SwatchColorHexCode,
            swatchImageUrl: productRefiner.SwatchImageUrl,
            name: StringExtensions.isNullOrEmpty(productRefiner.LeftValueBoundString) ? '' : productRefiner.LeftValueBoundString!,
            label: StringExtensions.isNullOrEmpty(productRefiner.LeftValueBoundLocalizedString)
                ? ''
                : productRefiner.LeftValueBoundLocalizedString!,
            isChecked: true,
            minValue: productRefiner.LeftValueBoundString,
            maxValue: productRefiner.RightValueBoundString
        });
    }
    return refinerItems;
};

/**
 * Convert an array of RefineItemData to ProductRefinerValue.
 * @param refineItemData - RefineItemData.
 * @returns - ProductRefinerValue[].
 */
export const convertRefinerItemDataToProductRefinerValue = (refineItemData: IRefineItemData[]): ProductRefinerValue[] => {
    const productRefinerValues: ProductRefinerValue[] = [];
    for (const refinerItem of refineItemData) {
        productRefinerValues.push({
            Count: refinerItem.count,
            DataTypeValue: refinerItem.dataTypeValue,
            LeftValueBoundString: refinerItem.minValue,
            RightValueBoundString: refinerItem.maxValue,
            RefinerRecordId: refinerItem.refinerItemId,
            UnitText: refinerItem.unitText,
            RowNumber: refinerItem.rowNumber,
            RefinerSourceValue: refinerItem.sourceValue,
            SwatchColorHexCode: refinerItem.swatchColorHexCode,
            SwatchImageUrl: refinerItem.swatchImageUrl
        });
    }
    return productRefinerValues;
};

/**
 * Converts IRefinerValue to IRefineItemData.
 * @param productRefinerValues - Array of IRefinerValue.
 * @returns - Array of IRefineItemData.
 */
export const convertToRefinerItemData = (productRefinerValues: IRefinerValue[]): IRefineItemData[] => {
    const refinerItemData: IRefineItemData[] = [];
    for (const productRefinerValue of productRefinerValues) {
        const temporaryRefinerItemData: IRefineItemData = {
            sourceValue: productRefinerValue.refinerSourceValue,
            swatchImageUrl: productRefinerValue.swatchImageUrl,
            swatchColorHexCode: productRefinerValue.swatchColorHexCode,
            refinerItemId: productRefinerValue.refinerRecordId ?? 0,
            dataTypeValue: productRefinerValue.dataTypeValue,
            name: productRefinerValue.leftValueBoundString || '',
            count: productRefinerValue.count,
            unitText: productRefinerValue.unitText,
            minValue: productRefinerValue.leftValueBoundString || '',
            maxValue: productRefinerValue.rightValueBoundString || '',
            label: productRefinerValue.leftValueBoundLocalizedString || '',
            isChecked: false
        };
        refinerItemData.push(temporaryRefinerItemData);
    }
    return refinerItemData;
};

/**
 * Method to map CommerceProperty object to custom ExtensionProperty.
 * @param commerceProperties - Commerce property object.
 * @returns Extension property array.
 */
const mapExtensionProperties = (commerceProperties: IExtensionProperty[]): CommerceProperty[] => {
    const extensionProperties: CommerceProperty[] = [];
    for (const item of commerceProperties) {
        extensionProperties.push({
            Key: item.propertyKey,
            Value: {
                ByteValue: item.value?.byteValue,
                BooleanValue: item.value?.isBooleanValue,
                DateTimeOffsetValue: item.value?.dateTimeOffsetValue,
                DecimalValue: item.value?.decimalValue,
                IntegerValue: item.value?.integerValue,
                LongValue: item.value?.longValue,
                StringValue: item.value?.stringValue
            }
        });
    }
    return extensionProperties;
};

/**
 * Method to map CommerceProperty object AttributeSwatch to custom AttributeSwatch.
 * @param attributeSwatches - Commerce property object.
 * @returns IAttributeSwatch array.
 */
const mapSwatchValues = (attributeSwatches: IAttributeSwatch[]): AttributeSwatch[] => {
    const swatches: AttributeSwatch[] = [];
    for (const swatch of attributeSwatches) {
        swatches.push({
            SwatchValue: swatch.swatchValue,
            SwatchImageUrl: swatch.swatchImageUrl,
            SwatchColorHexCode: swatch.swatchColorHexCode,
            IsDefault: swatch.isDefault,
            ProductImageUrls: swatch.productImageUrls,
            ExtensionProperties: swatch.extensionProperties ? mapExtensionProperties(swatch.extensionProperties) : []
        });
    }
    return swatches;
};

/**
 * Method to map CommerceProperty object AttributeValue to custom IAttributeValue.
 * @param attributeValues - Commerce property object.
 * @returns IAttributeValue array.
 */
const mapAttributeValues = (attributeValues: IAttributeValue[]): AttributeValue[] => {
    const attributes: AttributeValue[] = [];
    for (const attribute of attributeValues) {
        attributes.push({
            Name: attribute.name,
            KeyName: attribute.keyName,
            UnitOfMeasureSymbol: attribute.unitOfMeasureSymbol,
            DataTypeValue: attribute.dataTypeValue,
            BooleanValue: attribute.isBooleanValue,
            DateTimeOffsetValue: attribute.dateTimeOffsetValue,
            FloatValue: attribute.floatValue,
            IntegerValue: attribute.integerValue,
            TextValue: attribute.textValue,
            CurrencyValue: attribute.currencyValue,
            CurrencyCode: attribute.currencyCode,
            AttributeValueId: attribute.attributeValueId,
            RecordId: attribute.recordId,
            Swatches: attribute.swatches ? mapSwatchValues(attribute.swatches) : [],
            ExtensionProperties: attribute.extensionProperties ? mapExtensionProperties(attribute.extensionProperties) : []
        });
    }
    return attributes;
};

/**
 * Method to map CommerceProperty object ProductSearchResult to custom IProductSearchResult.
 * @param searchProducts - Commerce property object.
 * @returns IProductSearchResult array.
 */
export const convertProductsToProductSearchResult = (searchProducts: IProductSearchResult[]): ProductSearchResult[] => {
    const products: ProductSearchResult[] = [];
    for (const product of searchProducts) {
        products.push({
            ItemId: product.itemId,
            Name: product.name,
            Price: product.price,
            PrimaryImageUrl: product.primaryImageUrl,
            RecordId: product.recordId,
            AverageRating: product.averageRating,
            TotalRatings: product.totalRatings,
            Description: product.description,
            BasePrice: product.basePrice,
            MinVariantPrice: product.minVariantPrice,
            MaxVariantPrice: product.maxVariantPrice,
            AttributeValues: product.attributeValues ? mapAttributeValues(product.attributeValues) : [],
            IsMasterProduct: product.isMasterProduct,
            MasterProductId: product.masterProductId,
            DefaultUnitOfMeasure: product.defaultUnitOfMeasure,
            ExtensionProperties: product.extensionProperties ? mapExtensionProperties(product.extensionProperties) : []
        });
    }
    return products;
};

/**
 * Converts array of IRefinerHierarchy to array of IRefineSubMenuData.
 * @param productRefinerHierarchyArray - Array of IRefinerHierarchy.
 * @returns - Returns array of IRefineSubMenuData.
 */
export const convertToRefinerSubMenuHierarchyData = (productRefinerHierarchyArray: IRefinerHierarchy[]): IRefineSubMenuData[] => {
    const subMenuArray: IRefineSubMenuData[] = [];
    for (const productRefinerHierarchy of productRefinerHierarchyArray) {
        subMenuArray.push({
            refinerSubMenuId: productRefinerHierarchy.recordId,
            name: productRefinerHierarchy.keyName,
            dataTypeValue: productRefinerHierarchy.dataTypeValue,
            refinerTypeValue: productRefinerHierarchy.refinerTypeValue,
            displayTemplateValue: productRefinerHierarchy.displayTemplateValue,
            sourceValue: productRefinerHierarchy.sourceValue,
            keyName: productRefinerHierarchy.keyName,
            isDimension: productRefinerHierarchy.isDimension,
            values: convertToRefinerItemData(productRefinerHierarchy.values)
        });
    }
    return subMenuArray;
};

/**
 * Compress Refiners.
 * @param refiners - Array of IRefineItemData.
 * @returns - Return compressed refiners.
 */
const compressRefiners = (refiners: IRefineItemData[]) => {
    const compressedRefinerKeys = ['dataTypeValue', 'refinerItemId', 'sourceValue'];
    const compressedRefiners: (string | number)[][] = [];

    for (const refiner of refiners) {
        const compressedRefiner: (string | number)[] = [];
        for (const refinerKey of compressedRefinerKeys) {
            compressedRefiner.push(refiner[refinerKey]);
        }
        compressedRefiners.push(compressedRefiner);
    }

    return compressedRefiners;
};

/**
 * Build ListPage Url For RefineItem.
 * @param fullUrl - Full URL.
 * @param refiners - Array of IRefineItemData.
 * @param sortingColumns - Array of SortColumn.
 * @param skipCount - Skip count.
 * @returns - Returns the URL as string.
 */
export const buildListPageUrlForRefineItem = (
    fullUrl: URL,
    refiners?: IRefineItemData[],
    sortingColumns?: SortColumn[],
    skipCount?: number
): string => {
    // Refiner Change flow
    if (refiners !== undefined) {
        if (!ArrayExtensions.hasElements(refiners)) {
            fullUrl.searchParams.delete('refiners');
        } else {
            const sortedRefiners = refiners.sort((first, second) =>
                first.refinerItemId && second.refinerItemId && first.refinerItemId > second.refinerItemId ? 1 : -1
            );
            fullUrl.searchParams.set('refiners', JSON.stringify(compressRefiners(sortedRefiners)));
        }

        // Adding or removing a refiner means we want to always load page 1
        fullUrl.searchParams.delete('skip');
        return fullUrl.href;
    }

    if (sortingColumns !== undefined) {
        if (!ArrayExtensions.hasElements(sortingColumns)) {
            fullUrl.searchParams.delete('sorting');
        } else {
            fullUrl.searchParams.set('sorting', JSON.stringify(sortingColumns));
        }

        // Changing the sort means always load page 1
        fullUrl.searchParams.delete('skip');
        return fullUrl.href;
    }

    if (skipCount) {
        fullUrl.searchParams.set('skip', skipCount.toString());
    }

    return fullUrl.href;
};
